import { ReactNode, useRef } from 'react';

import { useScrollEdgeFade } from '@/hooks/scroll/use-scroll-edge-fade';
import { cn } from '@/lib/utils';

interface SummaryCardsCarouselProps {
  children: ReactNode;
}

/**
 * Horizontal snap carousel for a row of summary/stat cards — cards keep a
 * fixed width instead of stretching across the screen, and any overflow
 * (small screens or extra cards) scrolls with snapping and theme-aware edge
 * fades. Consumers: finance overheads summary, time-tracking approvals cards.
 */
export const SummaryCardsCarousel = ({ children }: SummaryCardsCarouselProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { showLeftFade, showRightFade } = useScrollEdgeFade(scrollRef);

  return (
    <div className="relative">
      <div
        ref={scrollRef}
        className={cn(
          'flex gap-4 overflow-x-auto pb-1',
          'snap-x snap-mandatory scroll-pl-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
        )}
      >
        {children}
      </div>

      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-background to-transparent transition-opacity duration-200',
          showLeftFade ? 'opacity-100' : 'opacity-0'
        )}
      />
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-background to-transparent transition-opacity duration-200',
          showRightFade ? 'opacity-100' : 'opacity-0'
        )}
      />
    </div>
  );
};

interface SummaryCardSlotProps {
  children: ReactNode;
  compact?: boolean;
}

/** Fixed-width snap slot for one summary card. */
export const SummaryCardSlot = ({ children, compact = false }: SummaryCardSlotProps) => (
  <div
    className={cn(
      'flex shrink-0 basis-[85%] snap-start',
      compact ? 'sm:basis-[320px] min-[1900px]:basis-[420px]' : 'sm:basis-[420px]'
    )}
  >
    <div className="flex-1">{children}</div>
  </div>
);
